import { zodResolver } from '@hookform/resolvers/zod';
import {
  CreateQuerySourceRequestSchema,
  type CreateQuerySourceRequest,
  type QuerySourceDetail,
  type QuerySourceListItem,
  UpdateQuerySourceRequestSchema,
  type UpdateQuerySourceRequest,
} from '@utazzo/shared';
import {
  UzButton,
  UzDrawer,
  UzForm,
  UzFormField,
  UzInput,
  UzNumberInput,
  UzSwitch,
  UzTextarea,
} from '@utazzo/ui';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { useCreateQuerySource, useUpdateQuerySource } from '../use-query-sources';

import { deriveCode, useAutoDeriveCode } from '@/components/master-code-field';

interface QuerySourceFormDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  querySource?: QuerySourceDetail | QuerySourceListItem | null;
}

const CREATE_DEFAULTS: CreateQuerySourceRequest = {
  code: '',
  name: '',
  description: '',
  sortOrder: 0,
  isActive: true,
};

/**
 * Field set shared by create + edit. Reads the form via context
 * (UzFormField → useFormContext), so it renders inside either UzForm.
 */
const QuerySourceFields = ({ pending }: { pending: boolean }): React.JSX.Element => (
  <>
    <UzFormField name="name">
      {({ field, error }) => (
        <UzInput
          {...field}
          value={(field.value as string | undefined) ?? ''}
          label="Name"
          placeholder="e.g. Website"
          error={error}
          required
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="description">
      {({ field, error }) => (
        <UzTextarea
          {...field}
          value={(field.value as string | undefined) ?? ''}
          label="Description"
          placeholder="Optional"
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="sortOrder">
      {({ field, error }) => (
        <UzNumberInput
          name={field.name}
          label="Sort order"
          precision={0}
          value={field.value === null || field.value === undefined ? '' : (field.value as number)}
          onValueChange={(v) => field.onChange(v ?? 0)}
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="isActive">
      {({ field }) => (
        <UzSwitch
          label="Active"
          description="Inactive sources are hidden from lead forms."
          checked={Boolean(field.value)}
          onCheckedChange={field.onChange}
          disabled={pending}
        />
      )}
    </UzFormField>
  </>
);

export const QuerySourceFormDrawer = ({
  open,
  onOpenChange,
  mode,
  querySource,
}: QuerySourceFormDrawerProps): React.JSX.Element => {
  const isCreate = mode === 'create';
  const createQuerySource = useCreateQuerySource();
  const updateQuerySource = useUpdateQuerySource();
  const pending = createQuerySource.isPending || updateQuerySource.isPending;

  const createForm = useForm<CreateQuerySourceRequest>({
    resolver: zodResolver(CreateQuerySourceRequestSchema),
    defaultValues: CREATE_DEFAULTS,
  });
  const updateForm = useForm<UpdateQuerySourceRequest>({
    resolver: zodResolver(UpdateQuerySourceRequestSchema),
    defaultValues: {},
  });

  useAutoDeriveCode(createForm, isCreate);

  useEffect(() => {
    if (!open) return;
    if (isCreate) {
      createForm.reset(CREATE_DEFAULTS);
    } else if (querySource) {
      // `code` is intentionally omitted - it's the stable machine
      // identifier and is read-only post-create.
      updateForm.reset({
        name: querySource.name,
        description: querySource.description ?? '',
        sortOrder: querySource.sortOrder,
        isActive: querySource.isActive,
      });
    }
  }, [open, isCreate, querySource, createForm, updateForm]);

  const onCreate = createForm.handleSubmit(async (values) => {
    // `code` is derived from the name (hidden field). Schema regex +
    // server uniqueness check still apply.
    await createQuerySource.mutateAsync({
      ...values,
      code: deriveCode(values.name),
      description: values.description?.trim() ? values.description : null,
    });
    onOpenChange(false);
  });

  const onUpdate = updateForm.handleSubmit(async (values) => {
    if (!querySource) return;
    await updateQuerySource.mutateAsync({
      id: querySource.id,
      payload: {
        ...values,
        description: values.description?.trim() ? values.description : null,
      },
    });
    onOpenChange(false);
  });

  const fields = <QuerySourceFields pending={pending} />;

  return (
    <UzDrawer
      open={open}
      onOpenChange={onOpenChange}
      title={isCreate ? 'Add query source' : `Edit ${querySource?.name ?? 'query source'}`}
      description="The name is what agents see."
      footer={
        <div className="flex justify-end gap-2">
          <UzButton variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancel
          </UzButton>
          <UzButton onClick={isCreate ? onCreate : onUpdate} loading={pending}>
            {isCreate ? 'Submit' : 'Save changes'}
          </UzButton>
        </div>
      }
    >
      {isCreate ? (
        <UzForm form={createForm} onSubmit={onCreate}>
          {fields}
        </UzForm>
      ) : (
        <UzForm form={updateForm} onSubmit={onUpdate}>
          {fields}
        </UzForm>
      )}
    </UzDrawer>
  );
};
